import React, { useEffect, useRef } from 'react';
import { useSetState } from 'ahooks';
import { Box, Button, Field, Form, Grid, Input, Message, Table, Overlay, NumberPicker } from '@fpxfd/next';
import $i18n from 'panda-i18n';

import FpxPage from '@/components/FpxPage';
import PageHeader from '@/components/PageHeader';
import FpxTable from '@/components/FpxTable';

import { Props, StatesProps, DetailItem, VASRecord, generalProps } from './index.interface';
import { getLogData, submitData } from './index.services';
import styles from './index.module.scss';
import { formatDate, transformToArray, transformToString, transformWareHouse, vToLabel } from '@/utils';
import FpxTab from '@/components/FpxTab';
import ActionArea from '@/components/ActionArea';
import {
  GENERAL_TAB,
  VAS_DEAL_TYPE,
  VAS_OPERATE_LOG,
  VAS_OPERATE_PERIOD,
  VAS_SERVICE_API,
  VAS_SERVICE_KIND,
  VAS_SERVICE_TYPE,
  VAS_STOCK_QUALITY,
  VAS_TASK_PERIOD,
} from '@/const';
import { GATEWAY_URL } from '@/config';
import dealFileUrl from '@/utils/dealFileUrl';
import { useLocation } from 'ice';
import FpxUpload from '@/components/FpxUpload';
import { queryRelativeNoApi } from '@/pages/VASOrder/VasOrderDetail/component/index.services';
import FpxDetail from '@/components/FpxDetailNew';
import AssociatedOrderCom from './AssociatedOrderCom';
import {
  trackFileUploadSuccess,
  trackFileUploadError,
  trackVASSubmitSuccess,
  trackVASSubmitError,
} from '@/utils/tracker';

const { Row, Col } = Grid;
const { Popup } = Overlay;

const VASInboundDetail = ({ consignmentNo, allDatas, callback }: Props) => {
  const uptRef: any = useRef();
  const { pathname } = useLocation();
  const [states, setStates] = useSetState<StatesProps>({
    basicList: [], // 基本信息
    vasList: [], // 增值服务数据
    customerAttachmentUrl: [], // 客户附件数据
    warehouseAttachmentUrl: [], // 结果附件数据
    generalList: [], // 综合信息数据
    logList: [], // 操作日志数据
    tabKey: 'res',
    fileUrlList: [],
    firstSet: true,
    cellArr: [],
    status: '', // 单状态
    inboundOrder: '',
    inboundBatchNumber: '', // 入库批次号
    mustUpload: false, // 是否必须上传文件
    remarkVisible: false, // 备注visible
    submitLoading: false,
    relativeData: {},
  });

  // 基础信息
  const basicItems = [
    {
      label: $i18n.get({ id: 'ServiceCategory.four_px', dm: '服务分类' }),
      dataIndex: 'businessType',
      transform: (value: string) => vToLabel(VAS_SERVICE_KIND, value),
    },
    {
      label: $i18n.get({ id: 'Warehouse.four_px', dm: '仓库' }),
      dataIndex: 'warehouseCode',
      transform: (value: string) => transformWareHouse(value),
    },
    {
      label: $i18n.get({ id: 'ValueAddedServiceNumber.four_px', dm: '增值服务单号' }),
      dataIndex: 'consignmentNo',
    },
    {
      label: $i18n.get({ id: 'ReferenceNumber.four_px', dm: '参考号' }),
      dataIndex: 'refNo',
    },
    {
      label: $i18n.get({ id: 'ValueAddedServices.four_px', dm: '增值服务' }),
      dataIndex: 'serviceType',
      transform: (value: string) => vToLabel(VAS_SERVICE_TYPE, value),
    },
    {
      label: $i18n.get({ id: 'Segment.four_px', dm: '所属分段' }),
      dataIndex: 'operationSegment',
      transform: (value: string) => vToLabel(VAS_OPERATE_PERIOD, value),
    },
    {
      label: $i18n.get({ id: 'State.four_px', dm: '状态' }),
      dataIndex: 'status',
      transform: (value: string) => vToLabel(VAS_TASK_PERIOD, value),
    },
    {
      label: $i18n.get({ id: 'ActualCompletionTime.four_px', dm: '实际完成时间' }),
      dataIndex: 'completeTime',
      transform: (value: string) => formatDate(value),
    },
    {
      label: $i18n.get({ id: 'KHBM.CustomerCode.four_px', dm: '客户编码' }),
      dataIndex: 'customerCode',
    },
    {
      label: $i18n.get({ id: 'CustomerRemarks.four_px', dm: '客户备注' }),
      dataIndex: 'customerRemark',
    },
    {
      label: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
      dataIndex: 'consignmentNo',
      slot: 'associatedOrder',
    },
  ];

  // 上传文件成功
  const onFileUploadSuccess = (file) => {
    const _fileUrl = `${file?.response?.data}^${file?.name}`;
    const fileUrlList = [...states.fileUrlList];
    fileUrlList.push(_fileUrl);
    setStates({
      fileUrlList,
    });
    file?.response?.data && Message.success($i18n.get({ id: 'FileUploaded.four_px', dm: '文件上传成功！' }));

    // 日志上传埋点
    trackFileUploadSuccess(consignmentNo, file?.name, file?.response);
  };
  // 上传文件失败
  const onFileUploadError = (file) => {
    // 日志上传埋点
    trackFileUploadError(consignmentNo, file?.name, file?.response);

    return Message.error(file?.response?.msg);
  };
  // 移除文件
  const onFileRemove = (file) => {
    const url = file?.url || file?.downloadURL || file?.imgURL;
    const fileUrlList = [...states.fileUrlList]?.filter((e) => !e?.includes(url));
    setStates({
      fileUrlList,
    });
  };

  // 查看url?
  const showAccessory = (value: string, index: number, record: VASRecord) => {
    const cellsData = states.cellArr?.filter((e: []) => e.length && e[0] === index)[0];
    const data = cellsData ? cellsData[1] : 1;

    return value ? (
      <Box direction="column" justify="center" align="center" className={styles.accessoryStyle}>
        <Button
          type="primary"
          text
          onClick={() => {
            window.open(dealFileUrl(value));
          }}
        >
          {$i18n.get({ id: 'DownloadUrl.four_px', dm: '下载Url' })}
        </Button>
        <span className={styles.accessoryTip}>
          {$i18n.get({ id: 'TotalNumberOfPrints.four_px', dm: '打印总数量' })}：
        </span>
        <span>
          {record?.operationQty}
          {$i18n.get({ id: 'Page.four_px', dm: '页' })}*{data}
          {$i18n.get({ id: 'F.A.four_px', dm: '份' })}={record?.actualOperationQty}
          {$i18n.get({ id: 'SkuTags.four_px', dm: '个SKU标签' })}
        </span>
      </Box>
    ) : (
      ''
    );
  };

  const showRemark = (value: string, index: number) => {
    if (!value) return '';
    return (
      <Popup
        trigger={
          <Button type="primary" text onClick={() => setStates({ remarkVisible: index })}>
            {value ? $i18n.get({ id: 'See.four_px', dm: '查看' }) : ''}
          </Button>
        }
        triggerType="click"
        hasMask
        align="cc cc"
        disableScroll
        visible={index === states.remarkVisible}
        onVisibleChange={(v: boolean) => {
          setStates({ remarkVisible: v });
        }}
        style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0px 2px 24px 0px rgb(207 209 216)',
        }}
      >
        <span className="overlay-demo">{value}</span>
      </Popup>
    );
  };
  // const showRemark = (value: string) => {
  //   return (
  //     <Button
  //       type="primary"
  //       text
  //       onClick={() => {
  //         value &&
  //           Dialog.show({
  //             content: value,
  //             footerActions: [],
  //             closeMode: 'mask',
  //           });
  //       }}
  //     >
  //       {value ? $i18n.get({ id: 'See.four_px', dm: '查看' }) : ''}
  //       {/* 查看 */}
  //     </Button>
  //   );
  // };

  // 获取数据
  const field = Field.useField();
  // 页签操作
  const gettab = (e: string) => {
    setStates({
      tabKey: e,
    });
  };

  const getAllData = (allDatas) => {
    const mustUpload = allDatas?.valueAddedTaskSkuDTOList.filter((e) => e?.operationCode === 'MFP').length;
    setStates({
      mustUpload,
      basicList: allDatas?.valueAddedTaskDTO, // 基本信息
      vasList: allDatas?.valueAddedTaskSkuDTOList || [], // 增值服务数据
      customerAttachmentUrl: transformToArray(allDatas?.valueAddedTaskDTO?.customerAttachmentUrl) || [], // 客户附件数据
      warehouseAttachmentUrl: transformToArray(allDatas?.valueAddedTaskDTO?.warehouseAttachmentUrl) || [],
      generalList: allDatas?.valueAddedTaskSkuDTOList || [], // 综合信息数据
      status: allDatas?.valueAddedTaskDTO?.status, // 单状态
      inboundOrder: allDatas?.valueAddedTaskDTO?.inboundOrder,
      inboundBatchNumber:
        allDatas?.valueAddedTaskSkuDTOList.length && allDatas?.valueAddedTaskSkuDTOList[0].inboundBatchNumber,
    });
    getLogData({ consignmentNo }).then((res) => {
      setStates({
        logList: res,
      });
    });
  };

  // 提交
  const submitAll = () => {
    field.validate((errors, values: any) => {
      if (errors) return;
      if (states.mustUpload && !states.fileUrlList?.length) {
        return Message.notice($i18n.get({ id: 'PleaseUploadTheFileFirst.four_px', dm: '请先上传文件！' }));
      }
      // 验证文件URL是否有效
      if (states.fileUrlList?.length) {
        const invalidFile = states.fileUrlList.find((fileStr) => {
          const url = fileStr?.split('^')[0];
          return url === 'undefined' || url === 'null' || !url;
        });
        if (invalidFile) {
          const fileName = invalidFile?.split('^')[1] || 'unknown file';
          return Message.error(
            $i18n.get({ id: 'UnableToUploadTheFilePlease_1010940675.four_px', dm: '文件上传失败，请重新上传！' }) +
              `: ${fileName}`,
          );
        }
      }
      setStates({
        submitLoading: true,
      });
      const req = {
        consignmentNo,
        operationDetailDTOList: states.vasList,
        valueAddedTaskSkuDTOList: states.generalList,
        warehouseAttachmentUrl: transformToString(states.fileUrlList),
        ...values,
      };

      submitData(req)
        .then((res) => {
          // 提交成功埋点
          trackVASSubmitSuccess(consignmentNo, req, res);

          callback()
            .then(() => {
              setStates({
                submitLoading: false,
              });
              Message.success($i18n.get({ id: 'TJCG！.SubmittedSuccessfully.four_px', dm: '提交成功！' }));
            })
            .catch(() => {
              setStates({
                submitLoading: false,
              });
            });
        })
        .catch((error) => {
          // 提交失败埋点
          trackVASSubmitError(consignmentNo, req, error);

          setStates({
            submitLoading: false,
          });
        });
    });
  };

  const dealCell = (dataSource: []) => {
    const arr = [...dataSource];
    const result: any = [];
    const typeArr = ['MFPSL', 'CSL', 'OTOC'];
    let index = 0;

    while (index < arr.length) {
      const m: generalProps = arr[index];
      const len = arr.filter((e: generalProps) => {
        return (
          typeArr.includes(e?.operationCode) &&
          e?.skuCode === m?.skuCode &&
          e?.productCode === m?.productCode &&
          e?.operationCode === m?.operationCode &&
          e?.actualSkuCode === m?.actualSkuCode &&
          e?.inboundBatchNumber === m?.inboundBatchNumber &&
          e?.stockQuality === m?.stockQuality
        );
      }).length;
      if (len > 1) result.push([index, len]);
      index += len || 1;
    }
    // arr.forEach((m: generalProps) => {
    //   const len = arr.filter((e: generalProps) => {
    //     return (
    //       typeArr.includes(e?.operationCode) &&
    //       e?.skuCode === m?.skuCode &&
    //       e?.productCode === m?.productCode &&
    //       e?.operationCode === m?.operationCode &&
    //       e?.actualSkuCode === m?.actualSkuCode &&
    //       e?.inboundBatchNumber === m?.inboundBatchNumber
    //     );
    //   }).length;
    //   if (len > 1) result.push([index, len]);
    // });

    setStates({
      firstSet: false,
      cellArr: result,
    });
  };

  const dealCellProps = (rowIndex: number, colIndex: number, dataIndex: string, record: any) => {
    const arr = [
      'skuCode',
      'productCode',
      'inboundBatchNumber',
      'stockQuality',
      'operationCode',
      'actualSkuCode',
      'customerRemark',
      'customerAttachmentUrl',
    ];
    for (let i = 0; i < states.cellArr.length; i++) {
      if (rowIndex === states.cellArr[i][0] && arr.includes(dataIndex)) {
        return {
          rowSpan: states.cellArr[i][1],
        };
      }
    }
  };

  useEffect(() => {
    getAllData(allDatas);
  }, [allDatas]);

  useEffect(() => {
    if (states.firstSet && states.vasList.length) {
      dealCell(states.vasList);
      dealCell(states.generalList);
    }
  }, [states.vasList, states.firstSet]);

  const handleUptBtn = () => {
    uptRef.current.changeApVisible(true);
  };

  const uptCallBack = () => {
    queryRelativeNo();
  };
  const queryRelativeNo = () => {
    queryRelativeNoApi({ consignmentNo }).then((res) => {
      setStates({ relativeData: res || {} });
    });
  };
  useEffect(() => {
    if (consignmentNo) {
      queryRelativeNo();
    }
  }, [consignmentNo]);

  return (
    <>
      {/* 基本信息、增值服务预报信息 */}
      <FpxPage
        header={
          <PageHeader
            title={$i18n.get({ id: 'ValueAddedOrders.four_px', dm: '增值订单' })}
            breadcrumbs={[
              { path: '', name: $i18n.get({ id: 'Order.four_px', dm: '订单' }) },
              { path: '/order/vasorder', name: $i18n.get({ id: 'ValueAddedOrders.four_px', dm: '增值订单' }) },
              {
                path: pathname,
                name: $i18n.get({ id: 'ValueAddedOrderDetails.four_px', dm: '增值订单详情' }),
              },
            ]}
            btnList={
              states.status === 'P'
                ? [
                    {
                      name: $i18n.get({ id: 'Submitted.four_px', dm: '提交' }),
                      callback: submitAll,
                      loading: states.submitLoading,
                    },
                  ]
                : []
            }
          />
        }
        detail={
          <FpxDetail
            title={$i18n.get({ id: 'BasicInformation.four_px', dm: '基本信息' })}
            items={basicItems}
            dataSource={states.basicList}
          >
            <div slot="associatedOrder">
              <Box direction="row" align="center">
                {states.relativeData?.allNos?.join(',')}
                <Button
                  text
                  type="primary"
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    handleUptBtn();
                  }}
                >
                  {$i18n.get({ id: 'Modify.four_px', dm: '修改' })}
                </Button>
              </Box>
            </div>
          </FpxDetail>
        }
        tableTitle={$i18n.get({ id: 'ValueAddedServiceForecastInformation.four_px', dm: '增值服务预报信息' })}
        table={
          <>
            {states.status === 'P' && (
              <>
                <Box className={styles.uploadVasFile}>
                  <span>{$i18n.get({ id: 'ResultAttachment.four_px', dm: '结果附件' })}</span>
                  <FpxUpload
                    action={`${GATEWAY_URL}${VAS_SERVICE_API}/valueAddedTask/uploadValueAddedTaskAttach`}
                    accept=".doc, .docx, .xls, .xlsx, .pdf, .rar, .zip, .png, .gif, .jpeg, .jpg"
                    onSuccess={onFileUploadSuccess}
                    onRemove={onFileRemove}
                    onError={onFileUploadError}
                  />
                </Box>
                <Form field={field} fullWidth style={{ marginBottom: '16px' }}>
                  <Row gutter={24}>
                    <Col span="7">
                      <Form.Item
                        label={$i18n.get({ id: 'WorkingHours.four_px', dm: '所用工时' })}
                        required
                        requiredMessage={$i18n.get({ id: 'BT.Required.four_px', dm: '必填' })}
                      >
                        <NumberPicker
                          step={0.1}
                          min={0}
                          style={{ width: '100%' }}
                          name="workingHour"
                          defaultValue={0}
                          innerAfter={<span style={{ marginRight: '6px' }}>H</span>}
                        />
                      </Form.Item>
                    </Col>
                    <Col span="7">
                      <Form.Item label={$i18n.get({ id: 'WarehouseRemarks.four_px', dm: '仓库备注' })}>
                        <Input name="warehouseRemark" trim />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
            <Table cellProps={dealCellProps} dataSource={states.vasList} fixedHeader maxBodyHeight={1000}>
              <Table.Column title={'No.'} width={100} cell={(value: string, index: number) => index + 1} lock />
              {!states.inboundBatchNumber && (
                <Table.Column
                  title={
                    <>
                      {$i18n.get({ id: 'BoxNo.four_px', dm: '箱序号' })}
                      <br />
                      {$i18n.get({ id: 'XTXM.BoxBarCode.four_px', dm: '箱条形码' })}
                    </>
                  }
                  width={200}
                  cell={(value: string, index: number, record: generalProps) => {
                    return (
                      <>
                        {record?.boxBarcode}
                        <br />
                        {record?.cartonNumber}
                      </>
                    );
                  }}
                />
              )}
              <Table.Column
                title={$i18n.get({ id: 'SkuEncoding.four_px', dm: 'SKU编码' })}
                dataIndex={'skuCode'}
                width={200}
              />
              <Table.Column
                title={$i18n.get({ id: 'ProductCode.four_px', dm: '商品编码' })}
                dataIndex={'productCode'}
                width={150}
              />
              <Table.Column
                title={
                  <>
                    {$i18n.get({ id: 'WarehouseReceiptNo.four_px', dm: '入库单号' })}
                    <br />
                    {$i18n.get({ id: 'BatchNumber.four_px', dm: '批次号' })}
                  </>
                }
                width={200}
                dataIndex="inboundBatchNumber"
                cell={(value: string, index: number, record: generalProps) => {
                  return (
                    <>
                      {states.inboundOrder}
                      <br />
                      {record?.inboundBatchNumber}
                    </>
                  );
                }}
              />
              <Table.Column
                title={$i18n.get({ id: 'InventoryQuality.four_px', dm: '库存质量' })}
                dataIndex={'stockQuality'}
                width={150}
                cell={(value: string) => vToLabel(VAS_STOCK_QUALITY, value)}
              />
              <Table.Column
                title={$i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' })}
                dataIndex={'operationCode'}
                width={200}
                cell={(value: string) => vToLabel(VAS_DEAL_TYPE, value)}
              />
              <Table.Column
                title={$i18n.get({ id: 'ReplacedSku.four_px', dm: '更换后SKU' })}
                dataIndex={'actualSkuCode'}
                width={200}
              />
              <Table.Column
                title={$i18n.get({ id: 'Number.four_px', dm: '数量' })}
                dataIndex={'operationQty'}
                width={150}
              />
              <Table.Column
                title={$i18n.get({ id: 'NumberOfTagsPerSkuNumber_2534406500.four_px', dm: '单个SKU贴标数/拍照数' })}
                dataIndex={'perSkuQty'}
                width={250}
              />
              <Table.Column
                title={$i18n.get({ id: 'TotalNumberOfOperations.four_px', dm: '操作总数量' })}
                dataIndex="actualOperationQty"
                width={150}
                cell={(value: string, index: number, record: generalProps) => {
                  if (record?.operationQty && record?.perSkuQty) {
                    return record.operationQty * record.perSkuQty;
                  }
                }}
              />
              <Table.Column
                title={$i18n.get({ id: 'Note.four_px', dm: '备注' })}
                dataIndex={'customerRemark'}
                width={150}
                cell={showRemark}
              />
              <Table.Column
                title={$i18n.get({ id: 'Annex.four_px', dm: '附件' })}
                dataIndex={'customerAttachmentUrl'}
                width={250}
                cell={showAccessory}
              />
            </Table>
          </>
        }
      />
      {/* 客户附件 */}
      <FpxPage
        tableTitle={$i18n.get({ id: 'CustomerAttachment.four_px', dm: '客户附件' })}
        table={
          <div className={styles.attachmentStyle} data-i18n-ignore-content>
            {states.customerAttachmentUrl?.map((e: string, i) => {
              const data = e.split('^');
              const url = data.length ? data[0] : '';
              const name = data.length ? data[1] : '';
              return (
                <Button type="primary" text key={`attachment${i}`} onClick={() => window.open(dealFileUrl(url))}>
                  {name}
                </Button>
              );
            })}
          </div>
        }
      />
      {/* 综合信息 */}
      {/* tab */}
      <FpxPage
        detail={
          <>
            <div className={styles.TableTitle}>
              {$i18n.get({ id: 'ComprehensiveInformation.four_px', dm: '综合信息' })}
            </div>
            <ul className={styles.attachmentStyle}>
              <li>{$i18n.get({ id: 'ResultAttachment.four_px', dm: '结果附件' })}</li>
              {states?.warehouseAttachmentUrl?.map((e) => {
                const url = e?.split('^')[0];
                // 校验URL是否合法,undefined或空字符串则不展示
                if (!url || url.trim() === '' || url.trim() === 'undefined') {
                  return null;
                }
                return (
                  <Button
                    type="primary"
                    text
                    key={`attachment${e}`}
                    onClick={() => {
                      window.open(dealFileUrl(url));
                    }}
                  >
                    {e?.split('^')[1]}
                  </Button>
                );
              })}
            </ul>
          </>
        }
        action={
          <ActionArea leftSection={<FpxTab dataSource={GENERAL_TAB} activeKey={states.tabKey} onChange={gettab} />} />
        }
        table={
          <>
            {states.status === 'C' && (
              <Box direction="row" justify="space-between" style={{ marginBottom: '16px' }}>
                <span style={{ width: '50%' }}>
                  {$i18n.get({ id: 'TotalWorkingHoursRequired.four_px', dm: '所需总工时' })}：
                  {states.basicList?.workingHour}
                </span>
                <span style={{ width: '50%' }}>
                  {$i18n.get({ id: 'WarehouseRemarks.four_px', dm: '仓库备注' })}：{states.basicList?.warehouseRemark}
                </span>
              </Box>
            )}
            {states.tabKey === 'res' ? (
              <Table
                cellProps={dealCellProps}
                dataSource={states.generalList}
                // hasBorder={false}
                fixedHeader
                maxBodyHeight={800}
              >
                <Table.Column title={'No.'} width={100} cell={(value: string, index: number) => index + 1} lock />
                {!states.inboundBatchNumber && (
                  <Table.Column
                    title={
                      <>
                        {$i18n.get({ id: 'BoxNo.four_px', dm: '箱序号' })}
                        <br />
                        {$i18n.get({ id: 'XTXM.BoxBarCode.four_px', dm: '箱条形码' })}
                      </>
                    }
                    width={200}
                    cell={(value: string, index: number, record: generalProps) => {
                      return (
                        <>
                          {record?.boxBarcode}
                          <br />
                          {record?.cartonNumber}
                        </>
                      );
                    }}
                  />
                )}
                <Table.Column
                  title={$i18n.get({ id: 'SkuEncoding.four_px', dm: 'SKU编码' })}
                  dataIndex={'skuCode'}
                  width={200}
                />
                <Table.Column
                  title={$i18n.get({ id: 'ProductCode.four_px', dm: '商品编码' })}
                  dataIndex={'productCode'}
                  width={150}
                />
                <Table.Column
                  title={
                    <>
                      {$i18n.get({ id: 'WarehouseReceiptNo.four_px', dm: '入库单号' })}
                      <br />
                      {$i18n.get({ id: 'BatchNumber.four_px', dm: '批次号' })}
                    </>
                  }
                  width={200}
                  dataIndex="inboundBatchNumber"
                  cell={(value: string, index: number, record: generalProps) => {
                    return (
                      <>
                        {states.inboundOrder}
                        <br />
                        {record?.inboundBatchNumber}
                      </>
                    );
                  }}
                />
                <Table.Column
                  title={$i18n.get({ id: 'InventoryQuality.four_px', dm: '库存质量' })}
                  dataIndex={'stockQuality'}
                  width={150}
                  cell={(value: string) => vToLabel(VAS_STOCK_QUALITY, value)}
                />
                <Table.Column
                  title={$i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' })}
                  dataIndex={'operationCode'}
                  width={200}
                  cell={(value: string) => vToLabel(VAS_DEAL_TYPE, value)}
                />
                <Table.Column
                  title={$i18n.get({ id: 'ReplacedSku.four_px', dm: '更换后SKU' })}
                  dataIndex={'actualSkuCode'}
                  width={200}
                />
                <Table.Column
                  title={$i18n.get({ id: 'Number.four_px', dm: '数量' })}
                  dataIndex={'operationQty'}
                  width={150}
                />
                <Table.Column
                  title={$i18n.get({ id: 'NumberOfTagsPerSkuNumber_2534406500.four_px', dm: '单个SKU贴标数/拍照数' })}
                  dataIndex={'perSkuQty'}
                  width={250}
                />
                <Table.Column
                  title={$i18n.get({ id: 'TotalNumberOfOperations.four_px', dm: '操作总数量' })}
                  dataIndex="actualOperationQty"
                  width={150}
                  cell={(value: string, index: number, record: generalProps) => {
                    if (record?.operationQty && record?.perSkuQty) {
                      return record.operationQty * record.perSkuQty;
                    }
                  }}
                />
              </Table>
            ) : (
              <FpxTable dataSource={states.logList} hasBorder={false} fixedHeader maxBodyHeight={600}>
                <Table.Column
                  title={$i18n.get({ id: 'Operator.four_px', dm: '操作人' })}
                  dataIndex={'operator'}
                  width={150}
                />
                <Table.Column
                  title={$i18n.get({ id: 'OperationTime.four_px', dm: '操作时间' })}
                  dataIndex={'createTime'}
                  width={150}
                />
                <Table.Column
                  title={$i18n.get({ id: 'MS.Description.four_px', dm: '描述' })}
                  dataIndex={'logType'}
                  width={200}
                  cell={(value: string) => vToLabel(VAS_OPERATE_LOG, value)}
                />
                <Table.Column title={$i18n.get({ id: 'Remarks', dm: '备注' })} dataIndex={'remark'} width={200} />
              </FpxTable>
            )}
          </>
        }
      />
      <AssociatedOrderCom
        consignmentNo={states.basicList?.consignmentNo}
        relativeData={states.relativeData}
        uptRef={uptRef}
        callBack={uptCallBack}
      />
    </>
  );
};

export default VASInboundDetail;
